import { ArrowRight, ShoppingCart } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { getGuestPickupLocations } from "../api/products";
import { CartItem } from "../components/CartItem";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { useCart } from "../context/CartContext";
import { formatCurrency } from "../utils/format";
import { safeSetItem } from "../utils/safeStorage";

export function CartPage() {
  const navigate = useNavigate();
  const { cartGSTTotal, cartGrandTotal, cartItems, cartSubtotal, error, loading } = useCart();
  const [pickupId, setPickupId] = useState("");
  const [pickupLocations, setPickupLocations] = useState([]);
  const [selectionError, setSelectionError] = useState("");

  useEffect(() => {
    let ignore = false;
    const controller = new AbortController();

    async function loadPickupLocations() {
      try {
        const response = await getGuestPickupLocations(controller.signal);
        if (!ignore) {
          setPickupLocations(response);
        }
      } catch (requestError) {
        if (!ignore) {
          setPickupLocations([]);
          setSelectionError("Pickup locations could not be loaded.");
        }
      }
    }

    loadPickupLocations();

    return () => {
      ignore = true;
      controller.abort();
    };
  }, []);

  const selectedPickup = useMemo(
    () => pickupLocations.find((item) => String(item.pickupId) === String(pickupId)) || null,
    [pickupId, pickupLocations]
  );

  function handleCheckout() {
    if (!cartItems.length) {
      setSelectionError("Cart cannot be empty.");
      return;
    }
    if (!selectedPickup) {
      setSelectionError("Please select a pickup address before checkout.");
      return;
    }
    if (cartGrandTotal <= 0) {
      setSelectionError("Cart total is invalid.");
      return;
    }

    const selection = {
      warehouse: selectedPickup.warehouse,
      warehouseCode: selectedPickup.warehouseCode,
      warehouseName: selectedPickup.warehouseName,
      warehouseAddress: selectedPickup.warehouseAddress,
      pickup: selectedPickup,
      subtotal: cartSubtotal,
      gstTotal: cartGSTTotal,
      grandTotal: cartGrandTotal,
    };

    console.log("selectedPickupLocation before navigation", selectedPickup);
    const savedSelection = safeSetItem("guestCheckoutSelection", JSON.stringify(selection));
    if (!savedSelection) {
      console.warn("Checkout selection could not be saved to browser storage.");
    }
    navigate("/checkout", { state: selection });
  }

  return (
    <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
      <section className="space-y-4">
        <Card className="glass-panel">
          <CardHeader className="border-b border-border/60">
            <CardTitle className="text-2xl">Your cart</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-6">
            {error ? (
              <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {error}
              </div>
            ) : null}
            {selectionError ? (
              <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {selectionError}
              </div>
            ) : null}

            {loading ? (
              <div className="rounded-3xl border border-dashed border-border bg-background/60 px-5 py-10 text-center text-muted-foreground">
                Loading cart...
              </div>
            ) : cartItems.length ? (
              cartItems.map((item) => <CartItem item={item} key={item.cartKey || `${item.productId}-${item.uomId || 0}`} />)
            ) : (
              <div className="rounded-3xl border border-dashed border-border bg-background/60 px-5 py-10 text-center">
                <ShoppingCart className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
                <p className="font-semibold">Your cart is empty</p>
                <p className="mt-2 text-sm text-muted-foreground">
                  Add a few products from the catalogue to get started.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </section>

      <aside>
        <Card className="glass-panel sticky top-24">
          <CardHeader className="border-b border-border/60">
            <CardTitle className="text-xl">Cart total</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 pt-6">
            <div className="space-y-4">
              <div>
                <label className="field-label" htmlFor="pickup">
                  Pickup Address
                </label>
                <select
                  className="field-input"
                  id="pickup"
                  onChange={(event) => {
                    setPickupId(event.target.value);
                    setSelectionError("");
                  }}
                  value={pickupId}
                >
                  <option value="">Select pickup address</option>
                  {pickupLocations.map((item) => (
                    <option key={item.pickupId} value={item.pickupId}>
                      {item.warehouse ? `${item.warehouse}` : ""} ({item.pickupAddress}) 
                    </option>
                  ))}
                </select>
                {selectedPickup ? (
                  <p className="mt-2 text-sm text-muted-foreground">
                    {selectedPickup.pickupAddress}
                    {/* {selectedPickup.warehouse ? ` | Warehouse: ${selectedPickup.warehouse}` : ""} */}
                  </p>
                ) : null}
              </div>

              <div className="rounded-3xl bg-secondary/70 p-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span>Subtotal</span>
                  <span>{formatCurrency(cartSubtotal)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span>GST</span>
                  <span>{formatCurrency(cartGSTTotal)}</span>
                </div>
                <div className="flex items-center justify-between border-t border-border/70 pt-2 text-base font-semibold">
                  <span>Grand Total</span>
                  <span>{formatCurrency(cartGrandTotal)}</span>
                </div>
              </div>
            </div>

            <Button className="w-full" disabled={!cartItems.length} onClick={handleCheckout} size="lg" type="button">
              Proceed to Checkout
              <ArrowRight className="h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      </aside>
    </div>
  );
}
